import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://yoybuy.cyou').replace(/\/$/, '');
const pagePath = '/yoybuy-sheet-guide';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Полное руководство по YOYbuy Sheet: как использовать таблицы для умного шоппинга из Китая',
  description: 'Узнайте всё о yoybuy spreadsheet — инновационном инструменте для организации заказов из Китая. Пошаговое руководство, преимущества, советы и все 14 категорий товаров.',
  author: { '@type': 'Organization', name: 'YOYbuy Sheet' },
  publisher: {
    '@type': 'Organization',
    name: 'YOYbuy Sheet',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
    },
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  url: `${siteUrl}${pagePath}`,
  inLanguage: 'ru',
  isAccessibleForFree: true,
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Подходит ли yoybuy spreadsheet для новичков?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Абсолютно! Yoybuy sheet спроектирован так, чтобы любой человек — даже без опыта заказа из Китая — мог легко разобраться. Достаточно скопировать ссылку на товар, вставить в таблицу, указать размер и количество.',
      },
    },
    {
      '@type': 'Question',
      name: 'Какие платформы поддерживает yoybuy sheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yoybuy spreadsheet работает со всеми основными китайскими маркетплейсами: Taobao, Tmall, Weidian, 1688, JD.com и другими.',
      },
    },
    {
      '@type': 'Question',
      name: 'Как рассчитывается стоимость доставки?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'В yoybuy sheet встроен калькулятор доставки, который учитывает вес товаров и выбранный способ отправки.',
      },
    },
    {
      '@type': 'Question',
      name: 'Безопасно ли использовать yoybuy spreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Да, yoybuy sheet — это просто инструмент для организации данных. Вы работаете с таблицей локально или в облаке, и только вы решаете, кому её отправлять.',
      },
    },
  ],
};

export default function YoybuySheetGuide() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <JsonLd data={faqSchema} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <header className="mb-10">
                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                  <Link to="/" className="hover:text-primary-600 transition-colors">Главная</Link>
                  <span>/</span>
                  <span className="text-foreground-700">Руководство</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                  Полное руководство по YOYbuy Sheet: как использовать таблицы для умного шоппинга из Китая
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-500">
                  Узнайте всё о <strong>yoybuy spreadsheet</strong> — инновационном инструменте для организации заказов из Китая. Пошаговое руководство, преимущества, советы и все 14 категорий товаров.
                </p>
                <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
                  <span>Обновлено: Июль 2026</span>
                  <span>·</span>
                  <span>Время чтения: 8 минут</span>
                </div>
              </header>

              <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed">
                {/* Section 1 */}
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Что такое YOYbuy Sheet и зачем он нужен?
                  </h2>
                  <p>
                    <strong>YOYbuy Sheet</strong> — это специализированная система электронных таблиц, созданная для упрощения процесса заказа товаров из Китая. В отличие от обычных агентов по закупкам, где вы отправляете ссылки и ждёте ответа, <strong>yoybuy spreadsheet</strong> даёт вам полный контроль над каждым этапом: от выбора товара до расчёта итоговой стоимости с доставкой.
                  </p>
                  <p>
                    Представьте, что вы хотите заказать партию кроссовок, несколько худи и пару сумок. Без <strong>yoybuy sheet</strong> вам пришлось бы вести переписку с агентом, уточнять цены, ждать ответов и постоянно проверять, всё ли учтено. С таблицей YOYbuy вы просто заполняете готовую форму: указываете ссылки на товары, размеры, цвета и количество — и система сама рассчитывает общую сумму, включая комиссию агента и стоимость международной доставки.
                  </p>
                  <p>
                    Главное преимущество <strong>yoybuy spreadsheet</strong> — прозрачность. Вы видите каждую цифру: цену товара в юанях, курс конвертации, комиссию, вес посылки и стоимость доставки. Никаких скрытых платежей и неожиданных наценок. Именно поэтому тысячи пользователей из Европы выбирают <strong>yoybuy sheet</strong> для организации своих покупок.
                  </p>
                </section>

                {/* Section 2 */}
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Как работает YOYbuy Spreadsheet: пошаговая инструкция
                  </h2>
                  <ol className="list-decimal pl-5 space-y-4">
                    <li>
                      <strong>Выберите категорию товара.</strong> На <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">главной странице yoybuy.cyou</Link> вы найдёте 14 категорий — от обуви до парфюмерии. Каждая категория в <strong>yoybuy sheet</strong> имеет свою оптимизированную структуру таблицы.
                    </li>
                    <li>
                      <strong>Скопируйте ссылки на товары.</strong> Найдите нужные товары на китайских платформах (Taobao, Weidian, 1688 и других) и скопируйте ссылки. Таблица YOYbuy поддерживает все основные маркетплейсы.
                    </li>
                    <li>
                      <strong>Заполните таблицу заказа.</strong> Вставьте ссылки, укажите размер, цвет, количество и особые пожелания. <strong>Yoybuy spreadsheet</strong> автоматически структурирует данные для агента.
                    </li>
                    <li>
                      <strong>Отправьте таблицу агенту.</strong> Готовую таблицу можно экспортировать и отправить вашему агенту по закупкам. Чёткая структура исключает ошибки и недопонимание.
                    </li>
                    <li>
                      <strong>Отслеживайте заказ.</strong> После отправки вы получаете подтверждение и можете отслеживать статус каждого товара через <strong>yoybuy sheet</strong>.
                    </li>
                  </ol>
                  <p className="mt-4">
                    Весь процесс занимает считанные минуты. По сравнению с традиционным способом заказа через переписку, <strong>yoybuy spreadsheet</strong> экономит часы времени и исключает риск ошибок при оформлении.
                  </p>
                </section>

                {/* Section 3 */}
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Все 14 категорий товаров в YOYbuy Sheet
                  </h2>
                  <p>
                    Одна из ключевых особенностей <strong>yoybuy sheet</strong> — удобная навигация по категориям. Каждая категория имеет собственную структуру таблицы, оптимизированную под конкретный тип товара:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    {[
                      { name: 'Обувь', desc: 'Кроссовки, кеды, ботинки — с сеткой размеров' },
                      { name: 'Худи / Свитеры', desc: 'Тёплые вещи с учётом плотности ткани' },
                      { name: 'Футболки', desc: 'Базовые и дизайнерские модели' },
                      { name: 'Куртки', desc: 'Верхняя одежда с размерами и утеплителем' },
                      { name: 'Брюки / Шорты', desc: 'Таблицы размеров и длины' },
                      { name: 'Головные уборы', desc: 'Кепки, шапки, панамы' },
                      { name: 'Комплекты', desc: 'Готовые наборы одежды' },
                      { name: 'Нижнее бельё', desc: 'Носки, бельё с размерной сеткой' },
                      { name: 'Джерси', desc: 'Спортивная форма и экипировка' },
                      { name: 'Аксессуары', desc: 'Очки, украшения, часы' },
                      { name: 'Сумки', desc: 'Рюкзаки, сумки, клатчи' },
                      { name: 'Ремни', desc: 'Кожаные и тканевые ремни' },
                      { name: 'Парфюмерия', desc: 'Духи и ароматы' },
                      { name: 'Разное', desc: 'Товары, не вошедшие в категории' },
                    ].map((item) => (
                      <div key={item.name} className="flex items-start gap-3 p-3 rounded-lg bg-background-100 border border-background-200">
                        <span className="w-1.5 h-1.5 mt-2 rounded-full bg-primary-500 flex-shrink-0"></span>
                        <div>
                          <span className="text-sm font-semibold text-foreground-900">{item.name}</span>
                          <span className="text-xs text-foreground-500 block mt-0.5">{item.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 4 */}
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Преимущества YOYbuy Spreadsheet перед обычными агентами
                  </h2>
                  <div className="space-y-4">
                    <div className="p-5 rounded-xl bg-primary-100/70 border border-primary-200">
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">Полная прозрачность цен</h3>
                      <p className="text-sm text-foreground-600">
                        В <strong>yoybuy sheet</strong> вы видите каждую составляющую стоимости: цену товара, комиссию агента, вес, стоимость доставки. Никаких скрытых наценок — всё как на ладони.
                      </p>
                    </div>
                    <div className="p-5 rounded-xl bg-accent-100/70 border border-accent-200">
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">Экономия времени</h3>
                      <p className="text-sm text-foreground-600">
                        Заполнение таблицы занимает минуты, а не часы. <strong>Yoybuy spreadsheet</strong> автоматизирует рутинные операции, позволяя вам сосредоточиться на выборе лучших товаров.
                      </p>
                    </div>
                    <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-200">
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">Минимум ошибок</h3>
                      <p className="text-sm text-foreground-600">
                        Чёткая структура таблицы исключает путаницу с размерами, цветами и количеством. Агент получает идеально оформленный заказ и обрабатывает его без лишних вопросов.
                      </p>
                    </div>
                    <div className="p-5 rounded-xl bg-background-200/70 border border-background-300">
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">14 категорий товаров</h3>
                      <p className="text-sm text-foreground-600">
                        Каждая категория в <strong>yoybuy sheet</strong> оптимизирована под конкретный тип товара. Таблица для обуви учитывает размерную сетку, для одежды — таблицу размеров, для аксессуаров — материал и цвет.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 5 */}
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Часто задаваемые вопросы о YOYbuy Sheet
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">
                        Подходит ли yoybuy spreadsheet для новичков?
                      </h3>
                      <p>
                        Абсолютно! <strong>Yoybuy sheet</strong> спроектирован так, чтобы любой человек — даже без опыта заказа из Китая — мог легко разобраться. Достаточно скопировать ссылку на товар, вставить в таблицу, указать размер и количество. Все подсказки уже встроены в шаблон таблицы.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">
                        Какие платформы поддерживает yoybuy sheet?
                      </h3>
                      <p>
                        <strong>Yoybuy spreadsheet</strong> работает со всеми основными китайскими маркетплейсами: Taobao, Tmall, Weidian, 1688, JD.com и другими. Вы можете комбинировать товары с разных платформ в одной таблице — это очень удобно для составления сборных заказов.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">
                        Как рассчитывается стоимость доставки?
                      </h3>
                      <p>
                        В <strong>yoybuy sheet</strong> встроен калькулятор доставки, который учитывает вес товаров и выбранный способ отправки. Вы сразу видите итоговую сумму и можете сравнить разные варианты доставки перед отправкой заказа агенту.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">
                        Безопасно ли использовать yoybuy spreadsheet?
                      </h3>
                      <p>
                        Да, <strong>yoybuy sheet</strong> — это просто инструмент для организации данных. Вы работаете с таблицей локально или в облаке, и только вы решаете, кому её отправлять. Никакие данные не передаются третьим лицам без вашего ведома.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 6 */}
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Советы по эффективному использованию YOYbuy Sheet
                  </h2>
                  <ul className="list-disc pl-5 space-y-3">
                    <li>
                      <strong>Группируйте товары по категориям.</strong> Используйте отдельные вкладки в <strong>yoybuy spreadsheet</strong> для разных категорий — это упростит обработку заказа агентом.
                    </li>
                    <li>
                      <strong>Проверяйте ссылки.</strong> Перед отправкой таблицы убедитесь, что все ссылки на товары активны и ведут на правильные страницы.
                    </li>
                    <li>
                      <strong>Указывайте альтернативы.</strong> Если товар может закончиться, добавьте в <strong>yoybuy sheet</strong> запасной вариант — агент сможет быстро заменить позицию без лишней переписки.
                    </li>
                    <li>
                      <strong>Используйте заметки.</strong> В таблице есть поле для примечаний — указывайте там особые пожелания по упаковке, проверке качества или маркировке.
                    </li>
                    <li>
                      <strong>Сравнивайте цены.</strong> <strong>Yoybuy spreadsheet</strong> позволяет легко сравнивать стоимость товаров у разных продавцов — выбирайте лучшее предложение.
                    </li>
                  </ul>
                </section>

                {/* Section 7 */}
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Заключение: почему стоит выбрать YOYbuy Sheet
                  </h2>
                  <p>
                    <strong>YOYbuy Sheet</strong> — это не просто таблица, а полноценный инструмент для умного шоппинга из Китая. Тысячи пользователей уже оценили удобство, прозрачность и скорость работы с <strong>yoybuy spreadsheet</strong>. Если вы хотите заказывать товары из Китая без лишних хлопот, скрытых комиссий и бесконечной переписки с агентами — начните использовать <strong>yoybuy sheet</strong> уже сегодня.
                  </p>
                  <p>
                    Вернитесь на <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">главную страницу yoybuy.cyou</Link>, чтобы выбрать категорию товаров и перейти к оформлению заказа. А если у вас остались вопросы — изучите другие разделы нашего руководства или свяжитесь с нами через основной сайт <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline font-medium">kaspreadsheet.com</a>.
                  </p>
                </section>
              </div>

              <div className="mt-12 pt-8 border-t border-background-300">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground-500">Было полезно? Поделитесь:</p>
                    <div className="flex items-center gap-3 mt-2">
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-twitter-x-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-telegram-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-facebook-line"></i>
                      </a>
                    </div>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Вернуться на главную
                  </Link>
                </div>
              </div>

              <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200">
                <p className="text-sm font-semibold text-foreground-900 mb-2">Другие полезные ссылки:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 underline">yoybuy.cyou — главная</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-shoes" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ обуви</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-hoodies" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ худи</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-jackets" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ курток</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-bags" className="text-xs text-primary-600 hover:text-primary-700 underline">Сумки</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-accessories" className="text-xs text-primary-600 hover:text-primary-700 underline">Аксессуары</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-safety" className="text-xs text-primary-600 hover:text-primary-700 underline">Безопасность</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-shipping" className="text-xs text-primary-600 hover:text-primary-700 underline">Доставка</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-vs-agents" className="text-xs text-primary-600 hover:text-primary-700 underline">Таблицы vs Агенты</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-taobao" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ с Taobao</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-sizes" className="text-xs text-primary-600 hover:text-primary-700 underline">Таблица размеров</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-primary-600 hover:text-primary-700 underline">kaspreadsheet.com</a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}